import React from 'react';
import { useForm } from 'react-hook-form';
import { UseFormSetError } from 'react-hook-form/dist/types/form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Input, Select, SubmissionButtons } from '@components/common';

import { useMutateKaiAssistants } from '@hooks/kaiAssistants/useMutateKaiAssistants';
import { useQueryKaiNotAssignedUsers } from '@hooks/kaiAssistants/useQueryKaiNotAssignedUsers';

import { isEmpty } from '@utils/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IShortUserInfo } from '@interfaces/user/user';

import { EValidationMode } from '@enums/validationMode';

import { getSchema } from './schema';

import styles from './KaiAssistantForm.module.scss';

export interface ISubmitHandlerProps {
	name: string;
	kaiId: string;
	id?: string;
	userIds?: string[];
}

export interface IKaiAssistantFormProps {
	id?: string;
	kaiId?: string;
	users?: IShortUserInfo[];
	organizationId: string;
	name?: string;
	close: () => void;
	isLoading?: boolean;
	submitHandler: (data: ISubmitHandlerProps, setError: UseFormSetError<{
		name: string;
		kaiId: string;
		id?: string;
		userIds?: string[];
	}>) => Promise<void>;
}

interface KaiAssistantForm {
	name: string;
	kaiId: string;
	id?: string;
	users?: TSelectOption<string>[] | null;
}

export default function KaiAssistantForm({
	id,
	kaiId,
	name,
	users,
	close,
	isLoading,
	submitHandler,
	organizationId,
}: IKaiAssistantFormProps) {
	const { handleCheckKaiAssistantName } = useMutateKaiAssistants();

	const {
		handleSubmit,
		register,
		setValue,
		setError,
		watch,
		formState: { errors },
		control,
	} = useForm<KaiAssistantForm>({
		mode: EValidationMode.onSubmit,
		resolver: yupResolver(getSchema({
			handleCheckName: handleCheckKaiAssistantName,
			organizationId,
			id,
		})),
		defaultValues: {
			name,
			kaiId,
			users: users?.map(user => {
				return {
					id: user.id,
					value: user.id,
					label: `${user.firstName} ${user.lastName}`,
				};
			}),
		},
	});

	const { kaiNotAssignedUsers } = useQueryKaiNotAssignedUsers(organizationId);

	const usersValue = watch('users');

	const usersOptions = kaiNotAssignedUsers.map(user => {
		return {
			id: user.id,
			value: user.id,
			label: `${user.firstName} ${user.lastName}`,
		};
	});

	const onChangeUsers = (options: TSelectOption<string>[]) => setValue('users', options);

	const onSubmit = async (data: KaiAssistantForm) => {
		await submitHandler({
			name: data.name,
			kaiId: data.kaiId,
			id,
			userIds: data.users?.map(user => user?.id as string) || [],
		}, setError);

		close();
	};

	return (
		<div className='display-flex flex-column flex-gap_20'>
			<Input
				containerClassName='mb-20'
				errorMessage={errors.name?.message}
				isError={!!errors.name?.message}
				label='Assistant’s short name'
				name='name'
				placeholder='Enter assistant’s short name'
				register={register}
				required
				setValue={setValue}
			/>
			<div>
				<Input
					errorMessage={errors.kaiId?.message}
					isError={!!errors.kaiId?.message}
					label='API token'
					name='kaiId'
					placeholder='Enter API token'
					register={register}
					required
					setValue={setValue}
				/>
				<div className={styles.inputDescription}>
					The API token for the Kai Assistant can be configured by the manager on the Kai platform.
					Please contact the manager for more details.
				</div>
			</div>
			<div>
				<Select
					closeMenuOnSelect={false}
					control={control}
					handleChange={onChangeUsers}
					isError={!!errors.users}
					isMulti
					label='Show assistant to'
					multiselectText='users chosen'
					name='users'
					options={usersOptions}
					placeholder='Choose users'
					value={usersValue}
				/>
				{!usersValue && (
					<div className={styles.inputDescription}>
						If no user is chosen, then this assistant won’t be shown to anyone (unless it is set as default)
					</div>
				)}
			</div>
			<SubmissionButtons
				disabledPositive={!isEmpty(errors)}
				isLoading={isLoading}
				negativeHandler={close}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Save'
			/>
		</div>
	);
}
